class CustomQuickLook extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;

    this.dialog =
      this.querySelector(
        '[data-quick-look-dialog]'
      );

    this.content =
      this.querySelector(
        '[data-quick-look-content]'
      );

    this.cache =
      new Map();

    this.abortController =
      null;

    this.activeTrigger =
      null;

    /*
     * This is deliberately separate from the button's disabled
     * property. Disabling an accelerated-checkout button during a
     * capture-phase click can stop Shopify/Razorpay receiving the
     * initiating click.
     */
    this.checkoutInProgress = false;
    this.checkoutLoadingTimeout = null;

    this.handleDocumentClick =
      this.handleDocumentClick.bind(this);

    this.handleCloseClick =
      this.handleCloseClick.bind(this);

    this.handleKeydown =
      this.handleKeydown.bind(this);

    this.handleContentClick =
      this.handleContentClick.bind(this);

    this.handleContentChange =
      this.handleContentChange.bind(this);

    this.handleVariantDropdownChange =
      this.handleVariantDropdownChange.bind(
        this
      );

    document.addEventListener(
      'click',
      this.handleDocumentClick
    );


    this.querySelectorAll(
      '[data-quick-look-close]'
    ).forEach(element => {

      element.addEventListener(
        'click',
        this.handleCloseClick
      );

    });

    this.handleQuickLookBuyNow =
      this.handleQuickLookBuyNow.bind(this);

    this.handleDialogCancel =
      this.handleDialogCancel.bind(this);

    this.handleGalleryKeydown =
      this.handleGalleryKeydown.bind(this);

    this.preloadedImages =
      new Set();

    this.pendingImagePreloads =
      new Map();

    this.handleNativeCartAddSuccess =
      this.handleNativeCartAddSuccess.bind(this);

    this.handleHorizonCartLinesUpdate =
      this.handleHorizonCartLinesUpdate.bind(this);

    this.addEventListener(
      'shopify:cart:lines-update',
      this.handleHorizonCartLinesUpdate
    );

    this.content?.addEventListener(
      'variant-dropdown:change',
      this.handleVariantDropdownChange
    );

    this.content?.addEventListener(
      'click',
      this.handleContentClick
    );

    this.content?.addEventListener(
      'change',
      this.handleContentChange
    );

    /*
     * Shopify's accelerated button can stop bubbling inside its own
     * component. Document capture sees the initiating click first,
     * before that component gets a chance to handle it.
     */
    document.addEventListener(
      'click',
      this.handleQuickLookBuyNow,
      true
    );

    this.dialog?.addEventListener(
      'keydown',
      this.handleKeydown
    );

    this.dialog?.addEventListener(
      'cancel',
      this.handleDialogCancel
    );

    this.content?.addEventListener(
      'keydown',
      this.handleGalleryKeydown
    );

  }

  startCartLoading(product, button) {
    if (!product || !button) return;

    product.classList.add('is-cart-adding');
    button.setAttribute('aria-busy', 'true');
    button.dataset.added = 'true';
  }

  stopCartLoading(product, button) {
    product?.classList.remove('is-cart-adding');

    if (!button) return;

    button.removeAttribute('aria-busy');
    button.removeAttribute('data-added');
  }

  handleNativeCartAddStart() {
    if (
      !this.dialog ||
      !this.dialog.open
    ) {
      return;
    }

    const product =
      this.content.querySelector(
        '.custom-quick-look-product'
      );

    const button =
      product?.querySelector(
        '.add-to-cart-button'
      );

    if (!button) return;

    button.disabled = true;
    this.startCartLoading(product, button);
  }


  handleNativeCartAddError() {
    const product =
      this.content.querySelector(
        '.custom-quick-look-product'
      );

    const button =
      product?.querySelector(
        '.add-to-cart-button'
      );

    if (button) {
      button.disabled = false;
    }

    this.stopCartLoading(product, button);
  }

  async handleNativeCartAddSuccess() {
    /*
     * Ignore unrelated events if Quick Look is not open.
     */
    if (
      !this.dialog ||
      !this.dialog.open
    ) {
      return;
    }

    /*
     * Horizon has successfully added the item.
     *
     * Give its ProductFormComponent one render cycle to
     * process returned cart sections / drawer state.
     */
    await new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

    /*
     * Close only Quick Look.
     *
     * Horizon already owns:
     * - cart state
     * - cart icon
     * - cart drawer refresh/open
     */
    await this.closeForCart();
  }

  handleHorizonCartLinesUpdate(event) {
    const productForm =
      event.target instanceof Element
        ? event.target.closest(
          'product-form-component[data-section-id="custom-quick-look"]'
        )
        : null;

    if (
      !productForm ||
      !this.contains(productForm) ||
      event.action !== 'add'
    ) {
      return;
    }

    /*
     * Horizon dispatches this immediately before its own /cart/add.js
     * request. Its promise resolves only after the native cart update
     * succeeds, or rejects on a network failure.
     */
    this.handleNativeCartAddStart();

    event.promise
      ?.then(result => {
        if (result?.detail?.didError) {
          this.handleNativeCartAddError();
          return;
        }

        this.handleNativeCartAddSuccess();
      })
      .catch(() => {
        this.handleNativeCartAddError();
      });
  }

  handleVariantDropdownChange(event) {
    const dropdown =
      event.target.closest(
        'custom-variant-dropdown'
      );

    if (!dropdown) {
      return;
    }


    /*
    * Only react to the Quick Look instance.
    * Product-card dropdowns must not affect
    * this modal.
    */
    if (
      dropdown.dataset.context !==
      'quick-look'
    ) {
      return;
    }


    const nativeSelect =
      dropdown.querySelector(
        '[data-quick-look-variant]'
      );


    if (!nativeSelect) {
      return;
    }


    this.changeVariant(
      nativeSelect
    );
  }

  preloadImage(url) {
    if (!url) return;

    /*
    * Already successfully requested.
    */
    if (
      this.preloadedImages.has(url)
    ) {
      return;
    }


    /*
    * Already being requested.
    */
    if (
      this.pendingImagePreloads.has(url)
    ) {
      return;
    }


    const image =
      new Image();


    image.decoding =
      'async';


    const cleanup =
      () => {

        this.pendingImagePreloads.delete(
          url
        );

      };


    image.onload =
      () => {

        this.preloadedImages.add(
          url
        );

        cleanup();

      };


    image.onerror =
      cleanup;


    this.pendingImagePreloads.set(
      url,
      image
    );


    image.src =
      url;
  }

  preloadVariantImages() {
    const selector =
      this.content.querySelector(
        '[data-quick-look-variant]'
      );

    if (!selector) {
      return;
    }


    const urls = [
      ...selector.options
    ]
      .map(option =>
        option.dataset.preloadImage
      )
      .filter(Boolean);


    /*
    * Remove duplicates.
    */
    const uniqueUrls =
      [...new Set(urls)];


    /*
    * Don't compete with initial rendering.
    *
    * Let the current modal/gallery load first,
    * then quietly preload alternative heroes.
    */
    const runPreload =
      () => {

        uniqueUrls.forEach(
          url => {
            this.preloadImage(url);
          }
        );

      };


    if (
      'requestIdleCallback' in window
    ) {

      window.requestIdleCallback(
        runPreload,
        {
          timeout: 1200
        }
      );

    } else {

      window.setTimeout(
        runPreload,
        350
      );

    }
  }

  handleGalleryKeydown(event) {
    if (
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    ) {
      return;
    }


    /*
    * Never hijack arrow keys while the user
    * is interacting with form controls.
    */
    if (
      event.target.closest(
        'input, select, textarea, ' +
        'quantity-selector-component, ' +
        '[role="slider"]'
      )
    ) {
      return;
    }


    const mediaArea =
      event.target.closest(
        '.custom-quick-look-product__media'
      );


    /*
    * Arrow navigation only applies while
    * focus is somewhere inside the gallery.
    */
    if (!mediaArea) {
      return;
    }


    const thumbnails = [
      ...mediaArea.querySelectorAll(
        '.slideshow-controls__thumbnail'
      )
    ].filter(thumbnail => {
      return !thumbnail.disabled &&
        !thumbnail.hidden;
    });


    if (thumbnails.length < 2) {
      return;
    }


    let currentIndex =
      thumbnails.findIndex(
        thumbnail =>
          thumbnail.getAttribute(
            'aria-selected'
          ) === 'true'
      );


    /*
    * Fallback if Horizon has not marked
    * the selected thumbnail for some reason.
    */
    if (currentIndex < 0) {
      currentIndex = 0;
    }


    const direction =
      event.key === 'ArrowRight'
        ? 1
        : -1;


    let nextIndex =
      currentIndex + direction;


    /*
    * Wrap around:
    *
    * last → first
    * first → last
    */
    if (nextIndex >= thumbnails.length) {
      nextIndex = 0;
    }

    if (nextIndex < 0) {
      nextIndex =
        thumbnails.length - 1;
    }


    const nextThumbnail =
      thumbnails[nextIndex];


    event.preventDefault();


    /*
    * Use Horizon's own thumbnail interaction
    * rather than manipulating the slideshow
    * ourselves.
    */
    nextThumbnail.click();


    /*
    * Keep keyboard focus synchronized with
    * the visible image.
    */
    nextThumbnail.focus({
      preventScroll: true
    });
  }

  closeForCart() {
    this.activeTrigger = null;
    this.resetCheckoutLoading();

    return new Promise(resolve => {

      if (
        !this.dialog ||
        !this.dialog.open
      ) {
        resolve();

        return;
      }


      this.dialog.classList.remove(
        'is-visible'
      );


      window.setTimeout(
        () => {

          /*
          * Close Quick Look first.
          */
          if (this.dialog.open) {
            this.dialog.close();
          }


          /*
          * Clear returned product HTML.
          */
          if (this.content) {
            this.content.innerHTML =
              '';
          }


          /*
          * Release Quick Look scroll lock.
          */
          document.documentElement.style.overflow =
            '';


          /*
            * Let the browser finish one layout cycle
            * after Quick Look has closed.
            */
          requestAnimationFrame(() => {

            resolve();

          });

        },
        180
      );

    });
  }

  handleQuickLookBuyNow(event) {
    const checkoutTarget =
      event.composedPath?.().find(element =>
        element instanceof Element &&
        element.matches(
          '.accelerated-checkout-block,' +
          'shopify-accelerated-checkout,' +
          'shopify-buy-it-now-button,' +
          '.shopify-payment-button'
        )
      ) ||
      (
        event.target instanceof Element
          ? event.target.closest('.accelerated-checkout-block')
          : null
      );

    if (!checkoutTarget || !this.contains(checkoutTarget)) return;

    /*
     * Let the first native click continue. Any subsequent keyboard or
     * programmatic click while checkout is launching is rejected.
     */
    if (this.checkoutInProgress) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    this.checkoutInProgress = true;

    /*
     * Do not interfere with Shopify / Razorpay.
     */
    this.activeTrigger = null;

    const product =
      this.content.querySelector(
        '.custom-quick-look-product'
      );

    product?.classList.add(
      'is-checkout-loading'
    );

    if (
      this.dialog &&
      !this.dialog.querySelector(
        '[data-quick-look-checkout-shield]'
      )
    ) {
      const shield = document.createElement('div');
      shield.className = 'custom-quick-look__checkout-shield';
      shield.dataset.quickLookCheckoutShield = '';
      shield.setAttribute('role', 'status');
      shield.setAttribute('aria-live', 'assertive');
      shield.innerHTML =
        '<span class="custom-quick-look__checkout-spinner" ' +
        'aria-hidden="true"></span>' +
        '<span>Opening secure checkout…</span>';
      this.dialog.append(shield);
    }

    product?.querySelectorAll(
      '.shopify-payment-button__button,' +
      'shopify-accelerated-checkout,' +
      'shopify-buy-it-now-button,' +
      'shopify-buy-it-now-button button'
    ).forEach(button => {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
    });

    /*
     * Safety reset.
     *
     * If accelerated checkout fails to open,
     * restore interaction instead of leaving
     * the Quick Look permanently disabled.
     */
    window.clearTimeout(
      this.checkoutLoadingTimeout
    );

    this.checkoutLoadingTimeout =
      window.setTimeout(
        () => {
          this.resetCheckoutLoading();
        },
        10000
      );
  }

  resetCheckoutLoading() {
    const product =
      this.content.querySelector(
        '.custom-quick-look-product'
      );

    const buttons =
      product?.querySelectorAll(
        '.shopify-payment-button__button,' +
        'shopify-buy-it-now-button,' +
        'shopify-buy-it-now-button button'
      );

    product?.classList.remove(
      'is-checkout-loading'
    );

    this.dialog?.querySelector(
      '[data-quick-look-checkout-shield]'
    )?.remove();

    buttons?.forEach(button => {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    });

    if (this.checkoutLoadingTimeout) {
      window.clearTimeout(
        this.checkoutLoadingTimeout
      );

      this.checkoutLoadingTimeout = null;
    }

    this.checkoutInProgress = false;
  }


  /* =====================================================
     PRODUCT CARD TRIGGER
     ===================================================== */

  handleDocumentClick(event) {
    const trigger =
      event.target.closest(
        '[data-custom-quick-look-trigger]'
      );

    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    const productUrl =
      trigger.dataset.productUrl;

    if (!productUrl) return;

    this.activeTrigger =
      trigger;

    this.open(productUrl);
  }


  /* =====================================================
     OPEN
     ===================================================== */

  async open(productUrl) {
    if (!this.dialog) return;


    this.showLoading();


    if (!this.dialog.open) {
      this.dialog.showModal();
    }


    requestAnimationFrame(() => {
      this.dialog.classList.add(
        'is-visible'
      );
    });


    document.documentElement.style.overflow =
      'hidden';


    try {

      const html =
        await this.getSection(
          productUrl
        );


      this.content.innerHTML =
        html;


      this.initializeContent();
      this.preloadVariantImages();
      this.focusInitialElement();

    } catch (error) {

      if (
        error.name !==
        'AbortError'
      ) {

        console.error(
          'Quick Look could not be loaded.',
          error
        );


        this.content.innerHTML = `
          <div style="
            padding:48px;
            text-align:center;
          ">
            Product information could not be loaded.
          </div>
        `;

      }

    }
  }

  focusInitialElement() {
    if (
      !this.dialog ||
      !this.dialog.open
    ) {
      return;
    }

    /*
    * Prefer the close button.
    *
    * This gives keyboard users an immediate
    * and predictable escape action without
    * jumping directly into product controls.
    */
    const closeButton =
      this.dialog.querySelector(
        '[data-quick-look-close]'
      );

    if (closeButton) {
      closeButton.focus({
        preventScroll: true
      });

      return;
    }

    const focusable =
      this.getFocusableElements();

    focusable[0]?.focus({
      preventScroll: true
    });
  }

  getFocusableElements() {
    if (!this.dialog) {
      return [];
    }

    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      'summary'
    ];

    return [
      ...this.dialog.querySelectorAll(
        selectors.join(',')
      )
    ].filter(element => {

      /*
      * Ignore hidden elements.
      */
      if (
        element.hidden ||
        element.getAttribute(
          'aria-hidden'
        ) === 'true'
      ) {
        return false;
      }

      /*
      * Ignore elements removed from layout.
      */
      const style =
        window.getComputedStyle(
          element
        );

      if (
        style.display === 'none' ||
        style.visibility === 'hidden'
      ) {
        return false;
      }

      return true;
    });
  }

  /* =====================================================
     FETCH
     ===================================================== */

  async getSection(productUrl) {

    const url =
      new URL(
        productUrl,
        window.location.origin
      );


    url.searchParams.set(
      'section_id',
      'custom-quick-look'
    );


    const cacheKey =
      url.toString();


    if (
      this.cache.has(
        cacheKey
      )
    ) {

      return this.cache.get(
        cacheKey
      );

    }


    if (
      this.abortController
    ) {

      this.abortController.abort();

    }


    this.abortController =
      new AbortController();


    const response =
      await fetch(
        url,
        {
          signal:
            this.abortController.signal,

          headers: {
            'X-Requested-With':
              'XMLHttpRequest'
          }
        }
      );


    if (!response.ok) {

      throw new Error(
        `Quick Look request failed: ${response.status}`
      );

    }


    const html =
      await response.text();


    this.cache.set(
      cacheKey,
      html
    );


    return html;
  }


  /* =====================================================
     CLOSE
     ===================================================== */

  handleCloseClick(event) {
    event.preventDefault();

    this.close();
  }


  close() {

    this.abortController?.abort();
    this.abortController = null;
    this.resetCheckoutLoading();

    if (
      !this.dialog ||
      !this.dialog.open
    ) {
      return;
    }

    this.dialog.classList.remove(
      'is-visible'
    );

    window.setTimeout(
      () => {

        this.dialog.close();

        this.content.innerHTML =
          '';

        document.documentElement.style.overflow =
          '';

        /*
        * Restore keyboard focus to the
        * product-card Quick Look button.
        */
        if (
          this.activeTrigger &&
          document.contains(
            this.activeTrigger
          )
        ) {
          this.activeTrigger.focus({
            preventScroll: true
          });
        }

        this.activeTrigger =
          null;

      },
      180
    );
  }


  handleKeydown(event) {
    if (
      !this.dialog ||
      !this.dialog.open
    ) {
      return;
    }


    /* =====================================================
      ESCAPE
      ===================================================== */

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();

      this.close();

      return;
    }


    /* =====================================================
      TAB FOCUS TRAP
      ===================================================== */

    if (event.key !== 'Tab') {
      return;
    }

    const focusable =
      this.getFocusableElements();

    if (!focusable.length) {
      event.preventDefault();

      return;
    }

    const first =
      focusable[0];

    const last =
      focusable[
      focusable.length - 1
      ];

    const active =
      document.activeElement;


    /*
    * Shift + Tab from first element
    * wraps to last.
    */
    if (
      event.shiftKey &&
      (
        active === first ||
        !this.dialog.contains(active)
      )
    ) {
      event.preventDefault();

      last.focus();

      return;
    }


    /*
    * Tab from last element
    * wraps back to first.
    */
    if (
      !event.shiftKey &&
      active === last
    ) {
      event.preventDefault();

      first.focus();
    }
  }

  handleDialogCancel(event) {
    /*
    * Prevent the browser from instantly
    * closing <dialog> so our custom close
    * animation can run.
    */
    event.preventDefault();

    this.close();
  }


  /* =====================================================
     LOADING
     ===================================================== */

  showLoading() {

    if (!this.content) return;


    this.content.innerHTML = `
      <div class="custom-quick-look__loading">

        <div
          class="custom-quick-look__skeleton"
        ></div>

        <div
          style="
            padding:52px 42px;
            display:grid;
            gap:18px;
          "
        >

          <div
            class="custom-quick-look__skeleton"
            style="
              height:34px;
              border-radius:5px;
            "
          ></div>

          <div
            class="custom-quick-look__skeleton"
            style="
              height:20px;
              width:55%;
              border-radius:5px;
            "
          ></div>

          <div
            class="custom-quick-look__skeleton"
            style="
              height:46px;
              border-radius:6px;
            "
          ></div>

          <div
            class="custom-quick-look__skeleton"
            style="
              height:48px;
              margin-top:20px;
              border-radius:6px;
            "
          ></div>

        </div>

      </div>
    `;
  }


  /* =====================================================
     INITIALIZE RETURNED CONTENT
     ===================================================== */

  initializeContent() {

    const selectedVariant =
      this.content.querySelector(
        '[data-quick-look-variant]'
      );


    if (
      selectedVariant
    ) {

      this.updateVariantState(
        selectedVariant
      );

    }
    this.initializeGalleryZoom();

  }


  /* =====================================================
     VARIANT CHANGE
     ===================================================== */

  handleContentChange(event) {
    const select =
      event.target.closest(
        '[data-quick-look-variant]'
      );

    if (!select) return;


    /*
    * Shared custom variant dropdown already emits
    * variant-dropdown:change and is handled through
    * handleVariantDropdownChange().
    */
    if (
      select.closest(
        'custom-variant-dropdown'
      )
    ) {
      return;
    }


    this.changeVariant(
      select
    );
  }


  async changeVariant(select) {
    const option =
      select.options[
      select.selectedIndex
      ];

    if (!option) return;


    const productUrl =
      option.dataset.variantUrl;

    if (!productUrl) return;


    /*
    * Immediately preload the hero belonging
    * to the newly selected variant.
    */
    const preloadUrl =
      option.dataset.preloadImage;

    if (preloadUrl) {
      this.preloadImage(
        preloadUrl
      );
    }


    /*
    * Prevent multiple simultaneous
    * variant section requests.
    */
    if (
      this.variantChangeInProgress
    ) {
      return;
    }


    this.variantChangeInProgress =
      true;


    const currentProduct =
      this.content.querySelector(
        '.custom-quick-look-product'
      );


    this.setVariantLoading(
      true
    );


    currentProduct?.classList.add(
      'is-changing'
    );


    try {

      const htmlPromise =
        this.getSection(
          productUrl
        );


      /*
      * Maintain a short minimum transition
      * so cached requests don't visually flash.
      */
      const minimumDelay =
        new Promise(resolve => {

          window.setTimeout(
            resolve,
            110
          );

        });


      const [html] =
        await Promise.all([
          htmlPromise,
          minimumDelay
        ]);


      /*
      * Render the new variant section.
      */
      this.content.innerHTML =
        html;


      const newProduct =
        this.content.querySelector(
          '.custom-quick-look-product'
        );


      /*
      * Start new content from its faded state.
      */
      newProduct?.classList.add(
        'is-changing'
      );


      /*
      * Reinitialize controls and preload the
      * remaining variant hero images.
      */
      this.initializeContent();

      this.preloadVariantImages();


      /*
      * Give the browser one paint before
      * revealing the new content.
      */
      requestAnimationFrame(() => {

        requestAnimationFrame(() => {

          newProduct?.classList.remove(
            'is-changing'
          );

          this.setVariantLoading(
            false
          );

        });

      });


    } catch (error) {

      currentProduct?.classList.remove(
        'is-changing'
      );


      this.setVariantLoading(
        false
      );


      console.error(
        'Quick Look variant could not be updated.',
        error
      );


    } finally {

      this.variantChangeInProgress =
        false;

    }
  }

  /* =====================================================
     QUANTITY / BUTTON EVENTS
     ===================================================== */

  handleContentClick(event) {
    const minus =
      event.target.closest(
        '[data-quick-look-quantity-minus]'
      );


    if (minus) {

      event.preventDefault();

      this.changeQuantity(
        -1
      );

      return;
    }


    const plus =
      event.target.closest(
        '[data-quick-look-quantity-plus]'
      );


    if (plus) {

      event.preventDefault();

      this.changeQuantity(
        1
      );

    }

    const descriptionToggle =
      event.target.closest(
        '[data-quick-look-description-toggle]'
      );

    if (descriptionToggle) {
      event.preventDefault();

      const description =
        descriptionToggle.closest(
          '[data-quick-look-description]'
        );

      if (!description) return;

      const isExpanded =
        description.classList.toggle(
          'is-expanded'
        );

      descriptionToggle.setAttribute(
        'aria-expanded',
        String(isExpanded)
      );

      descriptionToggle.textContent =
        isExpanded
          ? 'Read less'
          : 'Read more';

      return;
    }

    const openVariantDropdown =
      this.content.querySelector(
        '.custom-variant-dropdown-wrapper--quick-look ' +
        '.custom-variant-dropdown__trigger[aria-expanded="true"]'
      );


    if (
      openVariantDropdown &&
      !event.target.closest(
        '.custom-variant-dropdown-wrapper--quick-look'
      )
    ) {

      const dropdown =
        openVariantDropdown.closest(
          '.custom-variant-dropdown'
        );


      this.closeQuickLookVariantDropdown(
        dropdown
      );
    }
  }


  changeQuantity(amount) {

    const input =
      this.content.querySelector(
        '[data-quick-look-quantity]'
      );


    if (!input) return;


    const current =
      Number(
        input.value || 1
      );


    input.value =
      Math.max(
        1,
        current + amount
      );

  }


  /* =====================================================
     VARIANT STATE
     ===================================================== */

  updateVariantState(select) {

    const option =
      select.options[
      select.selectedIndex
      ];


    if (!option) return;


    const detailsLink =
      this.content.querySelector(
        '[data-quick-look-details]'
      );


    if (
      detailsLink &&
      option.dataset.variantUrl
    ) {

      detailsLink.href =
        option.dataset.variantUrl;

    }

  }

  setVariantLoading(isLoading) {
    const product =
      this.content.querySelector(
        '.custom-quick-look-product'
      );

    if (!product) return;


    product.classList.toggle(
      'is-variant-loading',
      isLoading
    );


    /*
    * Variant selector
    */
    const variantSelector =
      product.querySelector(
        '[data-quick-look-variant]'
      );

    if (variantSelector) {
      variantSelector.disabled =
        isLoading;

      variantSelector.setAttribute(
        'aria-busy',
        String(isLoading)
      );
    }


    /*
    * Horizon quantity controls
    */
    product.querySelectorAll(
      'quantity-selector-component button,' +
      'quantity-selector-component input'
    ).forEach(control => {

      control.disabled =
        isLoading;

    });


    /*
    * Add to Cart
    */
    const addToCart =
      product.querySelector(
        '.add-to-cart-button'
      );

    if (addToCart) {

      /*
      * Remember whether Shopify had already
      * disabled the button, e.g. sold out.
      */
      if (isLoading) {

        addToCart.dataset.wasDisabled =
          String(
            addToCart.disabled
          );

        addToCart.disabled =
          true;

        addToCart.setAttribute(
          'aria-busy',
          'true'
        );

      } else {

        /*
        * Do not enable something that was
        * already disabled before loading.
        */
        const wasDisabled =
          addToCart.dataset.wasDisabled ===
          'true';

        addToCart.disabled =
          wasDisabled;

        addToCart.removeAttribute(
          'aria-busy'
        );

        delete addToCart.dataset.wasDisabled;
      }
    }


    /*
    * Buy Now / accelerated checkout.
    *
    * Shopify's accelerated button is a custom
    * element, so disabling only the visible
    * <button> is the most reliable approach.
    */
    product.querySelectorAll(
      '.shopify-payment-button__button,' +
      'shopify-buy-it-now-button button'
    ).forEach(button => {

      if (isLoading) {

        button.dataset.wasDisabled =
          String(
            button.disabled
          );

        button.disabled =
          true;

        button.setAttribute(
          'aria-busy',
          'true'
        );

      } else {

        button.disabled =
          button.dataset.wasDisabled ===
          'true';

        button.removeAttribute(
          'aria-busy'
        );

        delete button.dataset.wasDisabled;
      }

    });
  }

  initializeGalleryZoom() {
    const mediaItems =
      this.content.querySelectorAll(
        '.custom-quick-look-product__media .product-media'
      );

    mediaItems.forEach(media => {
      const image =
        media.querySelector(
          '.product-media__image'
        );

      if (!image) return;

      /*
      * Avoid duplicate listeners after
      * variant rerenders.
      */
      if (
        image.dataset.quickLookZoomInitialized ===
        'true'
      ) {
        return;
      }

      image.dataset.quickLookZoomInitialized =
        'true';

      image.addEventListener(
        'click',
        () => {
          this.openImageZoom(image);
        }
      );
    });
  }

  openImageZoom(image) {
    if (!image) return;

    const source =
      image.dataset.maxResolution ||
      image.currentSrc ||
      image.src;

    if (!source) return;

    let zoomDialog =
      this.querySelector(
        '[data-quick-look-image-zoom]'
      );

    if (!zoomDialog) {
      zoomDialog =
        document.createElement('dialog');

      zoomDialog.className =
        'custom-quick-look-image-zoom';

      zoomDialog.setAttribute(
        'data-quick-look-image-zoom',
        ''
      );

      zoomDialog.innerHTML = `
        <button
          type="button"
          class="custom-quick-look-image-zoom__close"
          data-zoom-close
          aria-label="Close image zoom"
        >
          ×
        </button>

        <div
          class="custom-quick-look-image-zoom__content"
        >
          <div
            class="custom-quick-look-image-zoom__loader"
            data-zoom-loader
          ></div>

          <img
            class="custom-quick-look-image-zoom__image"
            data-zoom-image
            alt=""
          >
        </div>
      `;

      this.appendChild(zoomDialog);

      zoomDialog
        .querySelector('[data-zoom-close]')
        ?.addEventListener(
          'click',
          () => zoomDialog.close()
        );

      zoomDialog.addEventListener(
        'click',
        event => {
          if (event.target === zoomDialog) {
            zoomDialog.close();
          }
        }
      );
    }

    const zoomImage =
      zoomDialog.querySelector(
        '[data-zoom-image]'
      );

    const loader =
      zoomDialog.querySelector(
        '[data-zoom-loader]'
      );

    zoomImage.classList.remove(
      'is-loaded'
    );

    loader?.classList.remove(
      'is-hidden'
    );

    /*
    * Open immediately so interaction
    * feels responsive.
    */
    if (!zoomDialog.open) {
      zoomDialog.showModal();
    }

    /*
    * Load only the image the customer
    * explicitly requested to zoom.
    */
    const preload =
      new Image();

    preload.decoding = 'async';

    preload.onload = () => {
      zoomImage.src = source;
      zoomImage.alt = image.alt || '';

      loader?.classList.add(
        'is-hidden'
      );

      requestAnimationFrame(() => {
        zoomImage.classList.add(
          'is-loaded'
        );
      });
    };

    preload.onerror = () => {
      /*
      * Fall back to the already loaded
      * storefront resolution.
      */
      zoomImage.src =
        image.currentSrc ||
        image.src;

      zoomImage.alt =
        image.alt || '';

      loader?.classList.add(
        'is-hidden'
      );

      zoomImage.classList.add(
        'is-loaded'
      );
    };

    preload.src = source;
  }

  disconnectedCallback() {
    this.removeEventListener(
      'shopify:cart:lines-update',
      this.handleHorizonCartLinesUpdate
    );

    document.removeEventListener(
      'click',
      this.handleDocumentClick
    );

    this.content?.removeEventListener(
      'variant-dropdown:change',
      this.handleVariantDropdownChange
    );

    this.content?.removeEventListener(
      'click',
      this.handleContentClick
    );

    this.content?.removeEventListener(
      'change',
      this.handleContentChange
    );

    document.removeEventListener(
      'click',
      this.handleQuickLookBuyNow,
      true
    );

    this.content?.removeEventListener(
      'keydown',
      this.handleGalleryKeydown
    );

    this.dialog?.removeEventListener(
      'keydown',
      this.handleKeydown
    );

    this.dialog?.removeEventListener(
      'cancel',
      this.handleDialogCancel
    );

    this.querySelectorAll(
      '[data-quick-look-close]'
    ).forEach(element => {
      element.removeEventListener(
        'click',
        this.handleCloseClick
      );
    });

    this.abortController?.abort();
    this.abortController = null;

    window.clearTimeout(this.checkoutLoadingTimeout);
    this.checkoutLoadingTimeout = null;
    this.checkoutInProgress = false;

    this.initialized = false;
  }

}


if (
  !customElements.get(
    'custom-quick-look'
  )
) {

  customElements.define(
    'custom-quick-look',
    CustomQuickLook
  );

}
